import { Capacitor, registerPlugin } from '@capacitor/core'

/**
 * The iPhone camera, from the web side (Brian's NativeCamera.swift, Oct 18-22
 * 2026; the contract is docs/NATIVE-CAMERA-CONTRACT.md).
 *
 * In the app the native side owns the front camera, runs Apple Vision's person
 * segmentation and the Smooth filter, and hands Record each frame (a JPEG) with
 * its person mask. Record draws and records them from its own canvas, exactly
 * as it draws a browser camera, so takes save the same way on both.
 *
 * Two transports, the same commands:
 *   1. The Capacitor plugin `GGCamera` (the native phase builds this).
 *   2. Brian's prototype wrapper: `window.webkit.messageHandlers.ggcam` and the
 *      `window.__ggNativeCam` flag. Kept so his iPhone build keeps working.
 * Without either, `nativeCamera.available()` is false and Record uses
 * getUserMedia and MediaPipe in the page.
 */

export type SegQuality = 'fast' | 'balanced' | 'accurate'

export interface NativeStartOptions {
  /** Green screen: send the person mask with each frame. */
  mask: boolean
  /** Smooth: 0 off, 0.5 the switch's setting, up to 1. */
  beauty: number
  quality?: SegQuality
}

export interface NativeStartResult {
  /** Where frames are fetched from. Default `ggcam://frame/`. */
  frameUrl?: string
}

/** What the Capacitor plugin must implement (see the contract doc). */
export interface GGCameraPlugin {
  start(o: NativeStartOptions): Promise<NativeStartResult>
  setMode(o: { mask: boolean }): Promise<void>
  orient(): Promise<void>
  setBeauty(o: { amount: number }): Promise<void>
  setQuality(o: { level: SegQuality }): Promise<void>
  stop(): Promise<void>
  /** Share and Photos (nativeMedia.ts). */
  installedApps(): Promise<Partial<Record<ShareApp, boolean>>>
  openApp(o: { app: ShareApp; caption: string }): Promise<{ opened: 'app' | 'store' }>
  saveVideo(o: { data: string; name: string; type: string; caption?: string }): Promise<{ saved: boolean }>
  /** Record's camera wheel: one light selection tap per detent (UISelectionFeedbackGenerator). */
  haptic(): Promise<void>
}

export type ShareApp = 'instagram' | 'tiktok' | 'youtube'

declare global {
  interface Window {
    __ggNativeCam?: number | boolean
    __ggApps?: Partial<Record<ShareApp, boolean>> | null
    __ggSaved?: (ok: boolean) => void
    webkit?: { messageHandlers?: Record<string, { postMessage(body: unknown): void } | undefined> }
  }
}

export const GGCamera = registerPlugin<GGCameraPlugin>('GGCamera')

export const hasPlugin = (): boolean => {
  try { return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('GGCamera') } catch { return false }
}

const legacyHandler = (name: 'ggcam' | 'ggshare' | 'gghaptic') => {
  try { return window.webkit?.messageHandlers?.[name] ?? null } catch { return null }
}

/** Brian's wrapper (PrototypeView.swift). */
export const hasLegacyWrapper = (): boolean => !!(window.__ggNativeCam && legacyHandler('ggcam'))

export const legacyPost = (name: 'ggcam' | 'ggshare', body: Record<string, unknown>) => {
  try { legacyHandler(name)?.postMessage(body) } catch { /* the page carries on */ }
}

/** One decoded frame. `mask` is 'keep' when the native side reused the last one. */
export interface NativeFrame {
  seq: number
  bitmap: ImageBitmap | HTMLImageElement
  width: number
  height: number
  mask: { data: Uint8Array; width: number; height: number } | 'keep' | null
  visionMs: number
}

const MAGIC = 0x47474331 // "GGC1"
export const FRAME_HEADER = 36

/**
 * Decodes Brian's frame payload (little-endian after the magic):
 * "GGC1" · seq u32 · jpeg length u32 · mask w u32 · mask h u32 · frame w u32 ·
 * frame h u32 · Vision ms x10 u32 · flags u32 · JPEG · mask (w x h bytes, 255 = person).
 * flags bit 0: no mask bytes, keep the previous mask.
 */
export async function decodeFrame(buf: ArrayBuffer): Promise<NativeFrame | null> {
  if (buf.byteLength < FRAME_HEADER) return null
  const dv = new DataView(buf)
  if (dv.getUint32(0, false) !== MAGIC) return null
  const seq = dv.getUint32(4, true), jl = dv.getUint32(8, true)
  const mw = dv.getUint32(12, true), mh = dv.getUint32(16, true)
  const cw = dv.getUint32(20, true), ch = dv.getUint32(24, true)
  const visionMs = dv.getUint32(28, true) / 10, flags = dv.getUint32(32, true)
  const jpeg = new Blob([new Uint8Array(buf, FRAME_HEADER, jl)], { type: 'image/jpeg' })
  let bitmap: ImageBitmap | HTMLImageElement
  try { bitmap = await toBitmap(jpeg) } catch { return null }
  let mask: NativeFrame['mask'] = null
  if (mw && mh && buf.byteLength >= FRAME_HEADER + jl + mw * mh) {
    mask = { data: new Uint8Array(buf, FRAME_HEADER + jl, mw * mh), width: mw, height: mh }
  } else if (flags & 1) mask = 'keep'
  return { seq, bitmap, width: cw, height: ch, mask, visionMs }
}

const toBitmap = (blob: Blob): Promise<ImageBitmap | HTMLImageElement> =>
  typeof createImageBitmap === 'function'
    ? createImageBitmap(blob)
    : new Promise((ok, no) => {
        const u = URL.createObjectURL(blob), im = new Image()
        im.onload = () => { URL.revokeObjectURL(u); ok(im) }
        im.onerror = no
        im.src = u
      })

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * The native camera session: commands out, frames in. One at a time.
 * `onFrame` gets every new frame; `onFail` fires when frames never come
 * (20 failed fetches in a row), and Record falls back to the browser camera.
 */
export class NativeCameraSession {
  private on = false
  private frameUrl = 'ggcam://frame/'
  private fails = 0
  private viaPlugin = hasPlugin()

  constructor(
    private onFrame: (f: NativeFrame) => void,
    private onFail: (why: string) => void,
  ) {}

  get running() { return this.on }

  async start(o: NativeStartOptions) {
    if (this.on) return
    this.on = true
    this.fails = 0
    if (this.viaPlugin) {
      try {
        const r = await GGCamera.start(o)
        if (r?.frameUrl) this.frameUrl = r.frameUrl
      } catch (e) { this.on = false; this.onFail(String(e)); return }
    } else {
      legacyPost('ggcam', { cmd: 'start', mask: o.mask, beauty: o.beauty > 0 ? o.beauty : false })
    }
    void this.pump()
  }

  setMask(mask: boolean) {
    if (!this.on) return
    if (this.viaPlugin) void GGCamera.setMode({ mask }).catch(() => undefined)
    else legacyPost('ggcam', { cmd: 'mode', mask })
  }

  setBeauty(amount: number) {
    if (!this.on) return
    if (this.viaPlugin) void GGCamera.setBeauty({ amount }).catch(() => undefined)
    else legacyPost('ggcam', { cmd: 'beauty', amount })
  }

  setQuality(level: SegQuality) {
    if (this.viaPlugin) void GGCamera.setQuality({ level }).catch(() => undefined)
    else legacyPost('ggcam', { cmd: 'quality', level })
  }

  /** The interface turned; the camera turns its picture to match. */
  orient() {
    if (this.viaPlugin) void GGCamera.orient().catch(() => undefined)
    else legacyPost('ggcam', { cmd: 'orient' })
  }

  stop() {
    if (!this.on) return
    this.on = false
    if (this.viaPlugin) void GGCamera.stop().catch(() => undefined)
    else legacyPost('ggcam', { cmd: 'stop' })
  }

  /** Long-poll: the native side answers as soon as a frame newer than `since` exists. */
  private async pump() {
    let since = 0
    while (this.on) {
      let r: Response
      try {
        r = await fetch(`${this.frameUrl}?since=${since}&n=${Date.now()}`, { cache: 'no-store' })
      } catch (e) {
        if (++this.fails > 20) return this.fail(String(e))
        await sleep(80); continue
      }
      if (!this.on) return
      if (r.status === 204) continue
      if (r.status !== 200) {
        if (++this.fails > 20) return this.fail(`status ${r.status}`)
        await sleep(40); continue
      }
      let buf: ArrayBuffer
      try { buf = await r.arrayBuffer() } catch { continue }
      const f = await decodeFrame(buf)
      if (!f) continue
      since = f.seq
      this.fails = 0
      if (this.on) this.onFrame(f)
    }
  }

  private fail(why: string) {
    this.stop()
    this.onFail(why)
  }
}

export const nativeCamera = {
  /** True inside the app with the native camera (the plugin, or Brian's wrapper). */
  available: (): boolean => hasPlugin() || hasLegacyWrapper(),
  /**
   * A detent on the camera wheel (Oct 30, Brian: "haptic feedback on the wheel scroll with a light click, barely
   * noticeable"): the app's selection tap; Brian's wrapper's gghaptic handler; navigator.vibrate where a browser has it.
   */
  haptic: (): void => {
    if (hasPlugin()) { void GGCamera.haptic().catch(() => undefined); return }
    const h = legacyHandler('gghaptic')
    if (h) { try { h.postMessage('selection') } catch { /* */ } return }
    try { navigator.vibrate?.(5) } catch { /* */ }
  },
  session: (onFrame: (f: NativeFrame) => void, onFail: (why: string) => void) =>
    new NativeCameraSession(onFrame, onFail),
}
